import React, { useEffect, lazy } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { BreadcrumbSchema } from '../components/StructuredData';
import { ServiceNav } from '../components/ServiceNav';
export function CommercialCleaning() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen w-full font-sans bg-white flex flex-col" data-id="element-880">
      <SEOHead title="Commercial Cleaning | Smith & Jengs Cleaning Services" description="Professional commercial cleaning services in Bicester and Oxfordshire. Reliable, efficient, and tailored to your business needs." canonical="https://smithandjengs.com/commercial-cleaning" data-id="element-881" />
      <BreadcrumbSchema items={[{
      name: 'Home',
      url: 'https://smithandjengs.com/'
    }, {
      name: 'Commercial Cleaning',
      url: 'https://smithandjengs.com/commercial-cleaning'
    }]} data-id="element-882" />
      <Navbar data-id="element-883" />

      <main className="flex-1" data-id="element-884">
        {/* 1. Hero Banner */}
        <section className="relative w-full h-[55vh] min-h-[420px] max-h-[600px] overflow-hidden bg-[#1a2744]" data-id="element-885">
          <div className="absolute inset-0" data-id="element-886">
            <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80" alt="Modern clean office space" className="w-full h-full object-cover object-center" decoding="async" data-id="element-887" />
            {/* Dark gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a2744]/90 via-[#1a2744]/50 to-transparent" data-id="element-888" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a2744]/70 via-transparent to-transparent" data-id="element-889" />
          </div>

          <div className="relative z-10 h-full flex items-end px-6 md:px-12 pb-12 pt-32" data-id="element-890">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} className="max-w-7xl mx-auto w-full" data-id="element-891">
              <h1 className="font-bebas text-white leading-[0.85] tracking-wide" data-id="element-892">
                <span className="text-7xl md:text-[9rem] lg:text-[11rem] block" data-id="element-893">
                  COMMERCIAL
                </span>
                <span className="font-serif-playfair italic font-normal normal-case text-[#e91e8c] text-6xl md:text-[8rem] lg:text-[10rem] block -mt-2 md:-mt-6" data-id="element-894">
                  Cleaning
                </span>
              </h1>
            </motion.div>
          </div>
        </section>

        <ServiceNav data-id="element-895" />

        {/* 2. Description + Image Section */}
        <section className="w-full bg-white py-16 md:py-24 px-6 md:px-12" data-id="element-896">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center" data-id="element-897">
            {/* Left: Text */}
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="flex flex-col lg:max-w-md" data-id="element-898">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a2744] leading-tight" data-id="element-899">
                Ready for a spotless workspace? Get your free quote today
              </h2>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed" data-id="element-900">
                A clean workspace is essential for productivity and making a
                great impression on clients. We provide comprehensive commercial
                cleaning solutions tailored to your specific business needs,
                ensuring a spotless and hygienic environment for your team.
              </p>
            </motion.div>

            {/* Right: Image */}
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="w-full h-[400px] lg:h-[500px] rounded-sm overflow-hidden" data-id="element-901">
              <img src="https://cdn.magicpatterns.com/uploads/jJ8rqPBAtF8Zv1F3SffbkU/sj-website-commercial-img1.png" alt="Clean modern office interior" className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-902" />
            </motion.div>
          </div>
        </section>

        {/* 3. Services List Section */}
        <section className="w-full bg-white pb-16 md:pb-24 px-6 md:px-12" data-id="element-903">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center" data-id="element-904">
            {/* Left: Image */}
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="w-full h-[400px] lg:h-[500px] rounded-sm overflow-hidden order-2 lg:order-1" data-id="element-905">
              <img src="https://cdn.magicpatterns.com/uploads/hsYfHybdP8AFrJp4GWQcRc/sj-website-commercial-img2.png" alt="Clean office space" className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-906" />
            </motion.div>

            {/* Right: List */}
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="flex flex-col order-1 lg:order-2" data-id="element-907">
              <ul className="flex flex-col w-full" data-id="element-908">
                {['Daily or weekly office cleaning', 'Sanitization of high-touch areas', 'Restroom cleaning and restocking', 'Floor care and vacuuming'].map((item, i) => <li key={i} className="border-b border-gray-300 py-6 text-[#1a2744] text-lg md:text-xl font-medium first:pt-0" data-id="element-909">
                    {item}
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </section>

        {/* 4. CTA Section */}
        <section className="w-full bg-[#f8f9fa] py-16 px-6 md:px-12" data-id="element-910">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 text-center md:text-left" data-id="element-911">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2744] leading-tight" data-id="element-912">
              Click to set up Commercial Cleaning
            </h2>
            <Link to="/commercial-cleaning-quote" className="bg-[#e91e8c] hover:bg-[#d0187c] text-white px-8 py-4 rounded-full font-semibold tracking-wide transition-colors shadow-md flex items-center gap-3 whitespace-nowrap" data-id="element-913">
              Book Online <span className="text-xl leading-none" data-id="element-914">&rarr;</span>
            </Link>
          </motion.div>
        </section>
      </main>

      <Footer data-id="element-915" />
    </div>;
}